import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiOutage } from '../services/apiAvailability';

/**
 * Subtle connectivity strip — browser offline, API outage, and brief “back online” states.
 * Listens to `coursolo:connectivity` from offlineService / apiAvailability.
 */
export default function ConnectivityBanner() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(() => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return 'offline';
    if (isApiOutage()) return 'api-down';
    return 'idle';
  });
  // phase: idle | offline | api-down | online | syncing

  useEffect(() => {
    let hideTimer;

    const clearHide = () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = undefined;
      }
    };

    const onNative = () => {
      clearHide();
      if (!navigator.onLine) {
        setPhase('offline');
        return;
      }
      if (isApiOutage()) {
        setPhase('api-down');
        return;
      }
      setPhase('online');
      hideTimer = setTimeout(() => setPhase('idle'), 2800);
    };

    const onService = (event) => {
      const status = event?.detail?.status;
      clearHide();
      if (status === 'offline') {
        setPhase('offline');
        return;
      }
      if (status === 'api-down') {
        setPhase('api-down');
        return;
      }
      if (status === 'syncing') {
        setPhase('syncing');
        return;
      }
      if (status === 'api-up' || status === 'online' || status === 'synced' || status === 'sync-error') {
        if (status === 'api-up' || !isApiOutage()) {
          setPhase('online');
          hideTimer = setTimeout(() => setPhase(isApiOutage() ? 'api-down' : 'idle'), 2800);
        } else {
          setPhase('api-down');
        }
      }
    };

    window.addEventListener('online', onNative);
    window.addEventListener('offline', onNative);
    window.addEventListener('coursolo:connectivity', onService);

    return () => {
      clearHide();
      window.removeEventListener('online', onNative);
      window.removeEventListener('offline', onNative);
      window.removeEventListener('coursolo:connectivity', onService);
    };
  }, []);

  if (phase === 'idle') return null;

  const styles = {
    offline: {
      bar: 'bg-amber-500/95 text-white',
      label: t('common.connectivity.offline'),
    },
    'api-down': {
      bar: 'bg-amber-600/95 text-white',
      label: t('common.connectivity.apiDown'),
    },
    syncing: {
      bar: 'bg-slate-800/95 text-white',
      label: t('common.connectivity.syncing'),
    },
    online: {
      bar: 'bg-emerald-600/95 text-white',
      label: t('common.connectivity.online'),
    },
  }[phase] || null;

  if (!styles) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed top-0 inset-x-0 z-[60] px-3 py-2.5 text-center text-xs sm:text-sm font-medium shadow-sm backdrop-blur-sm ${styles.bar}`}
    >
      <span className="inline-flex items-center justify-center gap-2 max-w-3xl mx-auto">
        {phase === 'offline' || phase === 'api-down' ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.58 9M1.42 9a15.91 15.91 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0M12 20h.01" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : phase === 'syncing' ? (
          <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M21 12a9 9 0 11-6.22-8.56" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M5 12.55a11 11 0 0114.08 0M8.53 16.11a6 6 0 016.95 0M12 20h.01" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {styles.label}
      </span>
    </div>
  );
}
